import React from "react";
import { BarChart3, ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Database } from "@/integrations/supabase/types";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];

interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface FilterDropdownProps {
  value: string;
  onValueChange: (value: string) => void;
  options: FilterOption[];
  prefix: string;
  icon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
}

export const FilterDropdown = React.forwardRef<HTMLButtonElement, FilterDropdownProps>(
  (
    {
      value,
      onValueChange,
      options,
      prefix,
      icon,
      className,
      disabled,
      placeholder,
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const selectedOption = options.find(option => option.value === value);
    const displayValue = selectedOption?.label || placeholder || t('common.select', { defaultValue: 'Выберите...' });

    return (
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger
          ref={ref}
          className={cn(
            "bg-card min-w-[160px] h-10",
            "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            className
          )}
          aria-label={ariaLabel || `${prefix} ${displayValue}`}
          {...props}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {icon && <span className="flex-shrink-0 text-muted-foreground">{icon}</span>}
            <span className="text-sm font-medium text-foreground flex-shrink-0">
              {prefix}:
            </span>
            <span className="text-sm text-foreground truncate">
              {displayValue}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  {option.icon && (
                    <span className="flex-shrink-0">{option.icon}</span>
                  )}
                  <span>{option.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  }
);

FilterDropdown.displayName = "FilterDropdown";

// Status filter specific component
interface StatusFilterProps {
  value: ProcessingStatus | "all";
  onValueChange: (value: ProcessingStatus | "all") => void;
  className?: string;
  disabled?: boolean;
}

export const StatusFilter = ({ value, onValueChange, className, disabled }: StatusFilterProps) => {
  const { t } = useTranslation();
  const statusOptions: FilterOption[] = [
    { value: "all", label: t('dashboard.filters.status_all'), icon: null },
    { value: "processing", label: t('records.status.processing'), icon: <span className="text-blue-500">🔄</span> },
    { value: "completed", label: t('records.status.completed'), icon: <span className="text-green-500">✅</span> },
    { value: "cancelled", label: t('records.status.cancelled'), icon: <span className="text-muted-foreground">❌</span> },
    { value: "failed", label: t('records.status.failed'), icon: <span className="text-red-500">⚠️</span> },
    { value: "waiting", label: t('records.status.waiting'), icon: <span className="text-yellow-500">⏳</span> },
    { value: "queued", label: t('records.status.queued'), icon: <span className="text-purple-500">📄</span> },
  ];

  return (
    <FilterDropdown
      value={value}
      onValueChange={onValueChange}
      options={statusOptions}
      prefix={t('dashboard.filters.status_prefix')}
      icon={<BarChart3 size={16} />}
      className={className}
      disabled={disabled}
      aria-label={t('dashboard.filters.status_aria')}
    />
  );
};

// Sort filter specific component
type SortBy = "newest" | "oldest" | "name-asc" | "name-desc";

interface SortFilterProps {
  value: SortBy;
  onValueChange: (value: SortBy) => void;
  className?: string;
  disabled?: boolean;
}

export const SortFilter = ({ value, onValueChange, className, disabled }: SortFilterProps) => {
  const { t } = useTranslation();
  const sortOptions: FilterOption[] = [
    { value: "newest", label: t('dashboard.sort_newest'), icon: <span>📅↓</span> },
    { value: "oldest", label: t('dashboard.sort_oldest'), icon: <span>📅↑</span> },
    { value: "name-asc", label: t('dashboard.sort_name') + " А-Я", icon: <span>🔤</span> },
    { value: "name-desc", label: t('dashboard.sort_name') + " Я-А", icon: <span>🔤</span> },
  ];

  return (
    <FilterDropdown
      value={value}
      onValueChange={onValueChange}
      options={sortOptions}
      prefix={t('dashboard.filters.sort_prefix')}
      icon={<ArrowUpDown size={16} />}
      className={className}
      disabled={disabled}
      aria-label={t('dashboard.filters.sort_aria')}
    />
  );
};